/**
 * **走焦（focus）—— 可聚焦图 + 几何选点。**
 *
 * 键盘方向键 / 手柄十字键在可聚焦控件之间按**几何**选下一个；确认键激活当前焦点。
 * 焦点框本身是**叠加标记**，不是控件状态（见 `state.ts` 的说明）——
 * 控件只被告知“你被聚焦了/不聚焦了”，怎么画由它自己决定。
 *
 * 这一层是纯逻辑（不 import Pixi），所以空间选点能在 node 里断言。
 * DOM/手柄接线在 `screenHost.ts`。
 */

export interface FocusRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface Focusable {
  /** 稳定 id（同一个控件反复注册要用同一个） */
  readonly id: string;
  /** 当前包围盒（世界坐标即可，只要所有控件用同一套）。每帧可能变，所以是函数 */
  bounds(): FocusRect;
  activate(): void;
  setFocused(focused: boolean): void;
}

export type FocusDirection = 'up' | 'down' | 'left' | 'right';

export interface FocusCandidate {
  readonly id: string;
  readonly rect: FocusRect;
}

/** 垂直方向的偏离惩罚：同等主距下，正对着的比斜着的优先 */
const PERPENDICULAR_WEIGHT = 2;

function centerX(rect: FocusRect): number {
  return rect.x + rect.width / 2;
}
function centerY(rect: FocusRect): number {
  return rect.y + rect.height / 2;
}

/**
 * **几何选点。** 返回下一个焦点的 id（没有就 null）。
 *
 * - 只考虑中心落在方向半平面里的候选（主方向差值 > 0.5）
 * - 打分 = 主距 + `PERPENDICULAR_WEIGHT` × 垂距
 * - 没有当前焦点时选“最靠上、再最靠左”的那个
 */
export function pickNext(
  current: FocusRect | null,
  candidates: readonly FocusCandidate[],
  direction: FocusDirection,
): string | null {
  if (candidates.length === 0) return null;
  if (current === null) {
    const sorted = [...candidates].sort(
      (a, b) => a.rect.y - b.rect.y || a.rect.x - b.rect.x || a.id.localeCompare(b.id),
    );
    return sorted[0]?.id ?? null;
  }
  const horizontal = direction === 'left' || direction === 'right';
  const sign = direction === 'right' || direction === 'down' ? 1 : -1;
  const cx = centerX(current);
  const cy = centerY(current);
  let best: { id: string; score: number; perp: number } | null = null;
  for (const candidate of candidates) {
    const primary = horizontal
      ? (centerX(candidate.rect) - cx) * sign
      : (centerY(candidate.rect) - cy) * sign;
    if (primary <= 0.5) continue;
    const perp = Math.abs(horizontal ? centerY(candidate.rect) - cy : centerX(candidate.rect) - cx);
    const score = primary + PERPENDICULAR_WEIGHT * perp;
    if (
      best === null ||
      score < best.score - 0.5 ||
      (Math.abs(score - best.score) <= 0.5 && perp < best.perp)
    ) {
      best = { id: candidate.id, score, perp };
    }
  }
  return best?.id ?? null;
}

/** 可聚焦控件的注册表。每个屏幕的控件在 build 时注册、destroy 时注销 */
export class FocusRing {
  readonly #items = new Map<string, Focusable>();
  #focused: string | null = null;

  register(item: Focusable): () => void {
    this.#items.set(item.id, item);
    return () => {
      if (this.#items.get(item.id) === item) this.#items.delete(item.id);
      if (this.#focused === item.id) {
        item.setFocused(false);
        this.#focused = null;
      }
    };
  }

  get size(): number {
    return this.#items.size;
  }

  get focusedId(): string | null {
    return this.#focused;
  }

  /** 把焦点放到某个 id（null = 取消焦点） */
  focus(id: string | null): void {
    if (id === this.#focused) return;
    if (this.#focused !== null) this.#items.get(this.#focused)?.setFocused(false);
    this.#focused = null;
    if (id === null) return;
    const item = this.#items.get(id);
    if (item === undefined) return;
    this.#focused = id;
    item.setFocused(true);
  }

  /** 按方向移动焦点。返回有没有动 */
  move(direction: FocusDirection): boolean {
    const candidates: FocusCandidate[] = [...this.#items].map(([id, item]) => ({
      id,
      rect: item.bounds(),
    }));
    if (candidates.length === 0) return false;
    const current = this.#focused === null ? null : (this.#items.get(this.#focused)?.bounds() ?? null);
    const next = pickNext(current, candidates, direction);
    if (next === null || next === this.#focused) return false;
    this.focus(next);
    return true;
  }

  /** 激活当前焦点。返回有没有东西被激活 */
  activate(): boolean {
    if (this.#focused === null) return false;
    this.#items.get(this.#focused)?.activate();
    return true;
  }

  /** 把焦点放到最靠前的那个（第一次按方向键时用） */
  focusFirst(): boolean {
    const next = pickNext(null, [...this.#items].map(([id, item]) => ({ id, rect: item.bounds() })), 'down');
    if (next === null) return false;
    this.focus(next);
    return true;
  }

  clear(): void {
    if (this.#focused !== null) this.#items.get(this.#focused)?.setFocused(false);
    this.#focused = null;
    this.#items.clear();
  }
}
/**
 * **当前焦点环。** 和 i18n 的 `t()` / 输入画像一样是全局来源：控件太多，
 * 把 `FocusRing` 穿过每一个控件签名不值得。`screenHost` 建一个并 `setFocusRing`。
 */
let current: FocusRing | null = null;

export function setFocusRing(ring: FocusRing | null): void {
  current = ring;
}

export function currentFocusRing(): FocusRing | null {
  return current;
}
